import React, { useState, useRef, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import LuggageIcon from '@mui/icons-material/Luggage';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Custom components
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';
import { CardMedia } from '@mui/material';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

// Shared fleet data
import { vehicles, categories, VehicleType } from '../data/fleet';

// Map hash values to category names
const hashToCategory: { [key: string]: string } = {
  'sedan': 'Sedan',
  'sedans': 'Sedan',
  'suv': 'SUV',
  'suvs': 'SUV',
  'premium-suv': 'Premium SUV',
  'premium-suvs': 'Premium SUV',
  'luxury': 'Premium SUV',
  'buses': 'Buses',
  'bus': 'Buses',
  'vans': 'Buses',
};

const FleetPage: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const [category, setCategory] = useState('All');
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ [key: string]: boolean }>({});
  const [showReadMore, setShowReadMore] = useState<{ [key: string]: boolean }>({});
  const descriptionRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const seoData = pageSEO['/fleet'];
  const structuredData = generateStructuredData('/fleet');

  // Handle hash-based category selection
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.substring(1).toLowerCase(); // Remove '#' and convert to lowercase
      const mappedCategory = hashToCategory[hash];
      
      if (mappedCategory && categories.includes(mappedCategory)) {
        setCategory(mappedCategory);
        // Scroll to vehicles section after category is set and page is rendered
        // Wait longer to ensure MainLayout loading is complete (800ms) + content render time
        setTimeout(() => {
          const vehiclesSection = document.getElementById('fleet-vehicles');
          if (vehiclesSection) {
            requestAnimationFrame(() => {
              const header = document.querySelector('header');
              const headerHeight = header ? header.offsetHeight : 120;
              const elementTop = vehiclesSection.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementTop - headerHeight - 20;
              window.scrollTo({
                top: Math.max(0, offsetPosition),
                behavior: 'smooth'
              });
            });
          }
        }, 1000); // Wait for MainLayout loading + render time
      } else if (hash === 'fleet-categories' || hash === 'fleet-vehicles') {
        // Scroll to specific section if hash points to it directly
        setTimeout(() => {
          const targetSection = document.getElementById(hash);
          if (targetSection) {
            requestAnimationFrame(() => {
              const header = document.querySelector('header');
              const headerHeight = header ? header.offsetHeight : 120;
              const elementTop = targetSection.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementTop - headerHeight - 20;
              window.scrollTo({
                top: Math.max(0, offsetPosition),
                behavior: 'smooth'
              });
            });
          }
        }, 1000);
      }
    }
  }, [location.hash]);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setCategory(newValue);
  };

  const toggleDescription = (vehicleId: string) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [vehicleId]: !prev[vehicleId]
    }));
  };

  const filteredVehicles = category === 'All'
    ? vehicles
    : vehicles.filter(vehicle => vehicle.category === category);

  useEffect(() => {
    // Check if descriptions actually overflow after render
    const timer = setTimeout(() => {
      filteredVehicles.forEach(vehicle => {
        const ref = descriptionRefs.current[vehicle.id];
        if (ref && !expandedDescriptions[vehicle.id]) {
          const isOverflowing = ref.scrollHeight > ref.clientHeight;
          setShowReadMore(prev => ({
            ...prev,
            [vehicle.id]: isOverflowing
          }));
        }
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [category, filteredVehicles, expandedDescriptions]);

  return (
    <>
      <SEO 
        pathname="/fleet" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
        }}
      />
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 10, md: 12 },
          mb: 6,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(to right, rgba(26, 43, 60, 0.9) 0%, rgba(26, 43, 60, 0.8) 50%, rgba(26, 43, 60, 0.7) 100%)`,
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.1))',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 600,
                mb: 1,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}
            >
              OUR FLEET
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.2,
              }}
            >
            Our Fleet
          </Typography>
            <Typography
              variant="h5"
              sx={{
                maxWidth: 800,
                opacity: 0.9,
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
            Premium vehicles meticulously maintained to deliver comfort, reliability, and style
          </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 6,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
            Luxury Fleet Selection
          </Typography>
          <Divider sx={{ mb: 3 }} />
            <Typography
              paragraph
              sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}
            >
            The United Travel maintains a diverse fleet of premium vehicles to meet the varied needs of our corporate clients.
            Each vehicle in our fleet undergoes rigorous maintenance and cleaning protocols to ensure your comfort and safety.
            From executive sedans to spacious vans, our vehicles combine luxury with functionality, providing the perfect
            backdrop for productive travel time.
          </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
            Browse our selection below to find the ideal vehicle for your transportation needs.
          </Typography>
        </Paper>
        </motion.div>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 6,
            display: 'flex',
            justifyContent: 'center',
            borderRadius: 3,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
          }}
          id="fleet-categories"
        >
          <Tabs
            value={category}
            onChange={handleCategoryChange}
            textColor="primary"
            indicatorColor="primary"
            aria-label="vehicle categories"
          >
            {categories.map((cat) => (
              <Tab 
                key={cat} 
                value={cat} 
                label={cat === 'All' ? 'All Vehicles' : cat === 'Premium SUV' ? 'Premium SUVs' : cat === 'SUV' ? 'SUVs' : cat === 'Sedan' ? 'Sedans' : cat} 
              />
            ))}
          </Tabs>
        </Paper>

        <Box id="fleet-vehicles" sx={{ scrollMarginTop: '100px' }}>
          <GridContainer spacing={4}>
          {filteredVehicles.map((vehicle, index) => (
            <GridItem xs={12} md={6} key={vehicle.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-5px)',
                    }
                  }}
                >
                  {/* <Box
                    sx={{
                      bgcolor: 'grey.200',
                      height: 250,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {vehicle.name} Image Placeholder
                    </Typography>
                  </Box> */}

                  <Box sx={{ position: 'relative', overflow: 'hidden', height: '220px', alignItems: 'center',
                      justifyContent: 'center', display: 'flex' }}>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.5 }}
                    >
                      <CardMedia
                        component="img"
                        height="220"
                        image={vehicle.image}
                        alt={vehicle.name}
                        sx={{
                          transition: 'transform 0.5s',
                          objectFit: 'cover',
                          objectPosition: 'center',
                        }}
                      />
                    </motion.div>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        zIndex: 2,
                      }}
                    >
                      <Chip
                        label={vehicle.category}
                        size="medium"
                        color="secondary"
                        sx={{
                          fontWeight: 600,
                          px: 1,
                          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)'
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.common.black, 0.75),
                        color: 'white',
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.5, sm: 0.7 },
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                        minWidth: { xs: '90px', sm: '100px' },
                        textAlign: 'center',
                        lineHeight: 1.3,
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      <Box sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                        ₹{vehicle.totalPrice.toLocaleString('en-IN')}
                      </Box>
                      <Box sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, opacity: 0.9 }}>
                        Up to 300 KM
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ 
                    p: { xs: 2, sm: 2.5, md: 3 }, 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    minHeight: '480px'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start', 
                      mb: 2,
                      gap: 1
                    }}>
                      <Typography 
                        variant="h6" 
                        component="h2"
                        sx={{ 
                          fontWeight: 700,
                          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                          lineHeight: 1.4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                          flex: 1,
                          minWidth: 0
                        }}
                      >
                        {vehicle.name}
                      </Typography>
                      <Chip
                        label={vehicle.category}
                        color="primary"
                        size="small"
                        sx={{ 
                          fontWeight: 500,
                          flexShrink: 0,
                          height: 'fit-content'
                        }}
                      />
                    </Box>

                    <Box sx={{ 
                      mb: 2, 
                      minHeight: expandedDescriptions[vehicle.id] ? 'auto' : '80px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Typography
                        ref={(el) => {
                          if (!expandedDescriptions[vehicle.id]) {
                            descriptionRefs.current[vehicle.id] = el;
                          } else {
                            descriptionRefs.current[vehicle.id] = null;
                          }
                        }}
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                          lineHeight: 1.6,
                          mb: showReadMore[vehicle.id] && !expandedDescriptions[vehicle.id] ? 0.5 : 0,
                          display: expandedDescriptions[vehicle.id] ? 'block' : '-webkit-box',
                          WebkitLineClamp: expandedDescriptions[vehicle.id] ? 'unset' : 3,
                          WebkitBoxOrient: expandedDescriptions[vehicle.id] ? 'unset' : 'vertical',
                          overflow: expandedDescriptions[vehicle.id] ? 'visible' : 'hidden',
                          textOverflow: expandedDescriptions[vehicle.id] ? 'unset' : 'ellipsis',
                          height: expandedDescriptions[vehicle.id] ? 'auto' : undefined,
                        }}
                      >
                        {vehicle.description}
                      </Typography>
                      {showReadMore[vehicle.id] && (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => toggleDescription(vehicle.id)}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 600,
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            mt: 0.5,
                            display: 'inline-block',
                            alignSelf: 'flex-start',
                            flexShrink: 0
                          }}
                        >
                          {expandedDescriptions[vehicle.id] ? 'Read less' : 'Read more'}
                        </Link>
                      )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 2
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        flex: '1 1 calc(50% - 8px)',
                        minWidth: '140px'
                      }}>
                        <Box sx={{ 
                          mr: 1, 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          p: 0.7,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <PersonIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </Box>
                        <Typography 
                          variant="body2" 
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {vehicle.passengers} Passengers
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        px: { xs: 1.5, sm: 2 },
                        py: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        flex: '1 1 calc(50% - 8px)',
                        minWidth: '140px'
                      }}>
                        <Box sx={{ 
                          mr: 1, 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          p: 0.7,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <LuggageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </Box>
                        <Typography 
                          variant="body2" 
                          fontWeight={500}
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {vehicle.luggage} Luggage
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 1,
                      mb: 2,
                      pt: 1.5,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 0.75
                      }}>
                        <LocalOfferIcon 
                          fontSize="small" 
                          color="primary" 
                          sx={{ 
                            mt: 0.25,
                            flexShrink: 0
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            lineHeight: 1.4
                          }}
                        >
                          ₹{vehicle.hourlyRate}/HR (After 12 Hours)
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 0.75
                      }}>
                        <LocalOfferIcon 
                          fontSize="small" 
                          color="primary" 
                          sx={{ 
                            mt: 0.25,
                            flexShrink: 0
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            lineHeight: 1.4
                          }}
                        >
                          ₹{vehicle.perDayKm}/KM (After 300 KM)
                        </Typography>
                      </Box>
                    </Box>

                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        fontWeight: 600,
                        mb: 1
                      }}
                    >
                      Amenities:
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 0.75, 
                      mb: 2.5
                    }}>
                      {vehicle.amenities.map((amenity, idx) => (
                        <Chip
                          key={idx}
                          label={amenity}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            borderRadius: 1,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            height: { xs: '24px', sm: '28px' }
                          }}
                        />
                      ))}
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      gap: { xs: 1, sm: 1.5 },
                      mt: 'auto',
                      pt: 1
                    }} style={{ display: 'none' }}>
                      <Button
                        component={RouterLink}
                        to={`/fleet/${vehicle.id}`}
                        variant="outlined"
                        color="primary"
                        sx={{
                          flex: { xs: '1 1 auto', sm: '0 0 auto' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1.5, sm: 2 }
                        }}
                        style={{ display: 'none' }}
                      >
                        View Details
                      </Button>
                      <Button
                        component={RouterLink}
                        to={`/booking?vehicle=${vehicle.id}`}
                        variant="contained"
                        color="primary"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          flex: { xs: '1 1 auto', sm: '0 0 auto' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1.5, sm: 2 }
                        }}
                        style={{ display: 'none' }}
                      >
                        Book This Vehicle
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            </GridItem>
          ))}
        </GridContainer>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mt: 6,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
            Need a Custom Solution?
          </Typography>
            <Typography
              paragraph
              sx={{
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.8,
              }}
            >
            Our fleet extends beyond what's shown here. If you have specific requirements for executive travel or group transportation,
            our team can provide custom vehicle solutions tailored to your needs.
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ mt: 2 }}
          >
            Contact Our Fleet Specialists
          </Button>
        </Paper>
        </motion.div>
      </Container>
    </Box>
    </>
  );
};

export default FleetPage; 